import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, CheckCircle2, Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO_URL } from "../data/mock";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Reset Password — A.C.D.T.";
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token") || "";
    setToken(t);
    if (!t) setError("Token mancante. Richiedi un nuovo link di reset.");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La password deve essere di almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      setError("Le due password non coincidono.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate("/area-soci"), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail || "Errore durante il reset. Il link potrebbe essere scaduto.";
      setError(typeof detail === "string" ? detail : "Errore durante il reset.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7ff] via-white to-[#eff2ff] flex items-center justify-center p-5">
      <div
        data-testid="reset-password-page"
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] px-6 py-8 text-center">
          <img
            src={LOGO_URL}
            alt="A.C.D.T."
            className="h-14 w-14 mx-auto mb-3 rounded-lg bg-white p-1"
          />
          <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#fbbf24]">
            Sicurezza account
          </div>
          <h1 className="text-2xl font-extrabold text-white mt-1">Reimposta la password</h1>
        </div>

        <div className="p-6 md:p-8">
          {success ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-extrabold text-[#0a1f55]">Password aggiornata!</h2>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                Sarai reindirizzato al login tra pochi secondi.
              </p>
              <Button
                onClick={() => navigate("/area-soci")}
                data-testid="reset-password-go-login"
                className="mt-5 bg-[#0a1f55] hover:bg-[#102a6b] text-white font-bold h-11 px-6"
              >
                Vai al login
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                Inserisci una nuova password (minimo 8 caratteri). Il link ha validità di 1 ora e può
                essere usato una sola volta.
              </p>

              {error && (
                <div
                  data-testid="reset-password-error"
                  className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px]"
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <Label htmlFor="new-pwd" className="text-[#0a1f55] font-semibold">
                  Nuova password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="new-pwd"
                    data-testid="reset-password-new"
                    type={showPwd ? "text" : "password"}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Almeno 8 caratteri"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-[#0a1f55]"
                    aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-pwd" className="text-[#0a1f55] font-semibold">
                  Conferma password
                </Label>
                <Input
                  id="confirm-pwd"
                  data-testid="reset-password-confirm"
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ripeti la nuova password"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting || !token}
                data-testid="reset-password-submit"
                className="w-full h-11 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Aggiornamento…
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Imposta nuova password
                  </>
                )}
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/area-soci")}
                  data-testid="reset-password-back-login"
                  className="text-[12.5px] text-slate-500 hover:text-[#0a1f55] font-semibold"
                >
                  Torna al login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
